/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {Entity} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {capitalizeFirstLetter, isEqual} from '@ion/core/src/utils/StringUtil';
import {PartialSuccessType} from '@ion/dls/src/types/types';
import {formatErrorMessage, partialStatusMessageFormatter} from '@ion/dls/src/utils/ComponentUtils';

/**
 *
 * Types that could be used on user prompt notifications to potentially change colors,
 * icons, etc.
 */
export enum UserPromptTypes {
  Error = 'error',
  Warning = 'warning',
  Success = 'success'
};

/**
 *
 * Base options for error user prompts
 */
export const errorOptions = {
  display     : true,
  message     : 'common:errors.generic',
  actionLabel : 'common:retry',
  autoHide    : false,
  type        : UserPromptTypes.Error
};

/**
 *
 * Base options for success user prompts
 */
export const successOptions = {
  display             : true,
  message             : 'notifications:generic.success',
  actionLabel         : 'common:ok',
  autoHide            : true,
  partialSuccessState : null,
  type                : UserPromptTypes.Success
};

const addSuccessMessages = {
  [Entity.reports] : 'reports:reportCreatedSuccess'
};

const getAddSuccessMessage = (entity: Entity): string => {
  return addSuccessMessages[entity] || 'common:editors.shared.successCreated';
};

/**
 * Results for the successful add asset user prompt that extend the default options.
 *
 * ```typescript
 *
 *    dispatch(toggleUserPrompt(successAddOptions(Entity.role)));
 *
 * ```
 */
export const successAddOptions = (entity: Entity) => ({
  ...successOptions,
  message         : getAddSuccessMessage(entity),
  translateOption : {entity: capitalizeFirstLetter(entity)}
});

/**
 * Results for the error add asset user prompt that extend the default options.
 *
 * ```typescript
 *
 *    dispatch(toggleUserPrompt(errorAddOptions(Entity.role)));
 *
 * ```
 */
export const errorAddOptions = (entity: Entity, err?: ServiceError) => ({

  ...errorOptions,
  message         : !isNil(err) ? formatErrorMessage(err) : 'common:editors.shared.errorCreated',
  translateOption : {entity: capitalizeFirstLetter(entity)}
});

const editSuccessMessages = {
  [Entity.reports] : 'reports:reportEditedSuccess'
};

const getEditSuccessMessage = (entity: Entity): string => {
  return editSuccessMessages[entity] || 'common:editors.shared.successEdited';
};

/**
 *
 * Results for the successful edit asset user prompt that extend the default options.
 */
export const successEditOptions = (
  entity: Entity,
  state: PartialSuccessType = null,
  message = getEditSuccessMessage(entity)
) => ({
  ...successAddOptions(entity),
  partialSuccessState : state ? partialStatusMessageFormatter(state.results, state.items) : null,
  message,
  state
});

/**
 *
 * Results for the error add asset user prompt that extend the default options.
 */
export const errorEditOptions = (entity: Entity, err?: ServiceError) => ({
  ...errorAddOptions(entity, err),
  message : !isNil(err) ? formatErrorMessage(err) : 'common:editors.shared.errorEdited'
});
